import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Calculator, 
  LogOut,
  ArrowLeft,
  Loader2,
  Save,
  Gift,
  Car,
  Shield,
  Percent,
  Heart,
  CreditCard,
  Users,
  Calendar,
  Baby,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBenefits = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    max_benefit_percentage: 50,
    km_monthly_limit: 615,
    km_months: 12,
    insurance_max_percentage: 2,
    ppr_months: 12,
    health_insurance_cost: 55,
    health_insurance_dependent_cost: 25,
    health_insurance_months: 12,
    health_age_below_25: 44,
    health_age_25_35: 44,
    health_age_36_50: 55,
    health_age_51_64: 92,
    health_age_above_64: 92,
    meal_card_limit: 9.60,
    meal_months: 11,
    rv_first_year_discount: 0,
    ss_first_year_discount: 25,
    ss_second_year_discount: 50,
    ss_third_year_discount: 75,
    active: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/benefit-settings`, { headers: getAuthHeaders() });
      setFormData({
        max_benefit_percentage: response.data.max_benefit_percentage || 50,
        km_monthly_limit: response.data.km_monthly_limit || 615,
        km_months: response.data.km_months || 12,
        insurance_max_percentage: response.data.insurance_max_percentage || 2,
        ppr_months: response.data.ppr_months || 12,
        health_insurance_cost: response.data.health_insurance_cost || 50,
        health_insurance_dependent_cost: response.data.health_insurance_dependent_cost || 25,
        health_insurance_months: response.data.health_insurance_months || 12,
        health_age_below_25: response.data.health_age_below_25 ?? 44,
        health_age_25_35: response.data.health_age_25_35 ?? 44,
        health_age_36_50: response.data.health_age_36_50 ?? 55,
        health_age_51_64: response.data.health_age_51_64 ?? 92,
        health_age_above_64: response.data.health_age_above_64 ?? 92,
        meal_card_limit: response.data.meal_card_limit || 9.60,
        meal_months: response.data.meal_months || 11,
        rv_first_year_discount: response.data.rv_first_year_discount ?? 0,
        ss_first_year_discount: response.data.ss_first_year_discount ?? 25,
        ss_second_year_discount: response.data.ss_second_year_discount ?? 50,
        ss_third_year_discount: response.data.ss_third_year_discount ?? 75,
        active: response.data.active !== false
      });
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sessão terminada");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        max_benefit_percentage: parseFloat(formData.max_benefit_percentage),
        km_monthly_limit: parseFloat(formData.km_monthly_limit),
        km_months: parseInt(formData.km_months),
        insurance_max_percentage: parseFloat(formData.insurance_max_percentage),
        ppr_months: parseInt(formData.ppr_months),
        health_insurance_cost: parseFloat(formData.health_insurance_cost),
        health_insurance_dependent_cost: parseFloat(formData.health_insurance_dependent_cost),
        health_insurance_months: parseInt(formData.health_insurance_months),
        health_age_below_25: parseFloat(formData.health_age_below_25),
        health_age_25_35: parseFloat(formData.health_age_25_35),
        health_age_36_50: parseFloat(formData.health_age_36_50),
        health_age_51_64: parseFloat(formData.health_age_51_64),
        health_age_above_64: parseFloat(formData.health_age_above_64),
        meal_card_limit: parseFloat(formData.meal_card_limit),
        meal_months: parseInt(formData.meal_months),
        rv_first_year_discount: parseFloat(formData.rv_first_year_discount),
        ss_first_year_discount: parseFloat(formData.ss_first_year_discount),
        ss_second_year_discount: parseFloat(formData.ss_second_year_discount),
        ss_third_year_discount: parseFloat(formData.ss_third_year_discount),
        active: formData.active
      };

      await axios.post(`${API}/benefit-settings`, payload, { headers: getAuthHeaders() });
      toast.success("Configurações guardadas!");
      loadSettings();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao guardar";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-benefits">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Configuração Benefícios</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{user?.name}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        {/* Back Button & Title */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </Link>
          <div>
            <h1 className="font-manrope font-extrabold text-2xl text-foreground flex items-center gap-2">
              <Gift className="w-6 h-6 text-pink-500" />
              Benefícios Flexíveis
            </h1>
            <p className="text-muted-foreground">Configurar limites de benefícios para contratos</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Info Box */}
            <Card className="bg-pink-50 border-pink-200">
              <CardContent className="p-4">
                <p className="text-sm text-pink-800">
                  <strong>Benefícios Flexíveis</strong> são componentes do pacote salarial que podem ser 
                  isentos de IRS e Segurança Social até determinados limites. Configure aqui os valores 
                  máximos permitidos para cada tipo de benefício.
                </p>
              </CardContent>
            </Card>

            {/* Settings Form */}
            <Card className="bg-white border border-border/60 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Limites de Benefícios</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Max Benefit Percentage */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Percent className="w-5 h-5 text-blue-500" />
                    <Label className="font-medium">Percentagem Máxima de Benefícios</Label>
                  </div>
                  <div className="relative max-w-xs">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      className="font-mono pr-8"
                      value={formData.max_benefit_percentage}
                      onChange={(e) => setFormData({ ...formData, max_benefit_percentage: e.target.value })}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Percentagem máxima do salário bruto que pode ser atribuída como benefícios flexíveis.
                    O valor padrão é 50%.
                  </p>
                </div>

                {/* KM Monthly Limit */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-brand-500" />
                    <Label className="font-medium">Limite Mensal Despesas KM</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Valor Máximo (€)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="font-mono pl-8"
                          value={formData.km_monthly_limit}
                          onChange={(e) => setFormData({ ...formData, km_monthly_limit: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Meses de Pagamento</Label>
                      <Select 
                        value={(formData.km_months || 12).toString()} 
                        onValueChange={(v) => setFormData({ ...formData, km_months: parseInt(v) })}
                      >
                        <SelectTrigger className="font-mono">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 meses</SelectItem>
                          <SelectItem value="11">11 meses</SelectItem>
                          <SelectItem value="12">12 meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Valor máximo mensal isento para despesas de quilómetros.
                    O valor padrão é €615 (legislação portuguesa).
                  </p>
                </div>

                {/* Insurance Max Percentage (PPR) */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-purple-500" />
                    <Label className="font-medium">Real Vida Seguro</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Percentagem (%)</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          className="font-mono pr-8"
                          value={formData.insurance_max_percentage}
                          onChange={(e) => setFormData({ ...formData, insurance_max_percentage: e.target.value })}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Meses de Pagamento</Label>
                      <Select 
                        value={(formData.ppr_months || 12).toString()} 
                        onValueChange={(v) => setFormData({ ...formData, ppr_months: parseInt(v) })}
                      >
                        <SelectTrigger className="font-mono">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 meses</SelectItem>
                          <SelectItem value="11">11 meses</SelectItem>
                          <SelectItem value="12">12 meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Health Insurance Cost */}
                <div className="p-4 bg-brand-50 rounded-lg space-y-3 border border-brand-200">
                  <div className="flex items-center gap-2">
                    <Heart className="w-5 h-5 text-brand-500" />
                    <Label className="font-medium text-brand-800">Seguro de Saúde</Label>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-brand-600">Custo Titular fallback (€/mês)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="font-mono pl-8"
                          value={formData.health_insurance_cost}
                          onChange={(e) => setFormData({ ...formData, health_insurance_cost: e.target.value })}
                          data-testid="health-cost-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-brand-600">Custo por Agregado fallback (€/mês)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="font-mono pl-8"
                          value={formData.health_insurance_dependent_cost}
                          onChange={(e) => setFormData({ ...formData, health_insurance_dependent_cost: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-brand-600">Meses de Pagamento</Label>
                      <Select 
                        value={(formData.health_insurance_months || 12).toString()} 
                        onValueChange={(v) => setFormData({ ...formData, health_insurance_months: parseInt(v) })}
                      >
                        <SelectTrigger className="font-mono">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="11">11 meses</SelectItem>
                          <SelectItem value="12">12 meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Patamares por idade — usado quando a idade do titular/agregado é fornecida no simulador */}
                  <div className="border-t border-brand-200 pt-3 mt-2">
                    <Label className="font-medium text-brand-800 text-sm block mb-2">
                      Patamares por idade (€/mês por pessoa)
                    </Label>
                    <p className="text-xs text-brand-600 mb-3">
                      Quando é fornecida a idade do titular/agregado no simulador, é usado o escalão correspondente. Caso contrário, é usado o valor "fallback" acima.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {[
                        { key: "health_age_below_25", label: "< 25 anos" },
                        { key: "health_age_25_35", label: "25 — 35" },
                        { key: "health_age_36_50", label: "36 — 50" },
                        { key: "health_age_51_64", label: "51 — 64" },
                        { key: "health_age_above_64", label: "> 64 anos" },
                      ].map(p => (
                        <div key={p.key} className="space-y-1">
                          <Label className="text-[11px] text-brand-700">{p.label}</Label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className="font-mono pl-6 h-8 text-sm"
                              value={formData[p.key]}
                              onChange={(e) => setFormData({ ...formData, [p.key]: e.target.value })}
                              data-testid={`health-tier-${p.key}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Meal Card Limit */}
                <div className="p-4 bg-orange-50 rounded-lg space-y-3 border border-orange-200">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-orange-500" />
                    <Label className="font-medium text-orange-800">Limite Diário Cartão Refeição</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    <div>
                      <Label className="text-xs text-orange-600 mb-1 block">Valor Diário (€)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="font-mono pl-8"
                          value={formData.meal_card_limit}
                          onChange={(e) => setFormData({ ...formData, meal_card_limit: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-orange-600 mb-1 block">Meses de Pagamento</Label>
                      <Select 
                        value={(formData.meal_months || 11).toString()} 
                        onValueChange={(v) => setFormData({ ...formData, meal_months: parseInt(v) })}
                      >
                        <SelectTrigger className="font-mono">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 meses</SelectItem>
                          <SelectItem value="11">11 meses</SelectItem>
                          <SelectItem value="12">12 meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs text-orange-600">
                    Valor diário isento para cartão refeição. Normalmente pago 11 meses (excluindo férias).
                  </p>
                </div>

                {/* SS Discount per Activity Year */}
                <div className="p-4 bg-blue-50 rounded-lg space-y-3 border border-blue-200">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    <Label className="font-medium text-blue-800">Descontos SS por Ano de Atividade (Freelancers)</Label>
                  </div>
                  <p className="text-xs text-blue-600">
                    Percentagem da taxa normal de SS aplicada em cada ano. Após o 3º ano paga-se a taxa cheia (21,4%).
                    <br />
                    <span className="font-medium">Recibos Verdes 1º ano:</span> por lei (Cód. Reg. Contributivos, art. 153.º-A) = <strong>0% (isenção total)</strong>. B2B usa a sua própria %.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-blue-700">RV 1º Ano (%)</Label>
                      <div className="relative">
                        <Input
                          type="number" min="0" max="100" step="1"
                          className="font-mono pr-8"
                          value={formData.rv_first_year_discount}
                          onChange={(e) => setFormData({ ...formData, rv_first_year_discount: e.target.value })}
                          data-testid="rv-y1-input"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                      <p className="text-[10px] text-blue-600">Por lei: 0 = isento</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-blue-700">B2B 1º Ano (%)</Label>
                      <div className="relative">
                        <Input
                          type="number" min="0" max="100" step="1"
                          className="font-mono pr-8"
                          value={formData.ss_first_year_discount}
                          onChange={(e) => setFormData({ ...formData, ss_first_year_discount: e.target.value })}
                          data-testid="ss-y1-input"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                      <p className="text-[10px] text-blue-600">Padrão B2B: 25%</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-blue-700">2º Ano (%)</Label>
                      <div className="relative">
                        <Input
                          type="number" min="0" max="100" step="1"
                          className="font-mono pr-8"
                          value={formData.ss_second_year_discount}
                          onChange={(e) => setFormData({ ...formData, ss_second_year_discount: e.target.value })}
                          data-testid="ss-y2-input"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                      <p className="text-[10px] text-blue-600">Padrão: 50%</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-blue-700">3º Ano (%)</Label>
                      <div className="relative">
                        <Input
                          type="number" min="0" max="100" step="1"
                          className="font-mono pr-8"
                          value={formData.ss_third_year_discount}
                          onChange={(e) => setFormData({ ...formData, ss_third_year_discount: e.target.value })}
                          data-testid="ss-y3-input"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                      <p className="text-[10px] text-blue-600">Padrão: 75%</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        A guardar...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Configurações
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBenefits;
